import React from "react";
import { Link } from "react-router-dom";
import { Scale, ShieldCheck, AlertTriangle, BookOpen, ArrowRight, ExternalLink } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

export default function Legal() {
  return (
    <div className="min-h-screen bg-background bg-grain">
      <PublicHeader ctaTo="/contatti" ctaLabel="Parla con noi" />

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16 animate-fade-in" data-testid="legal-page">
        {/* Hero */}
        <section className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-4">
            <Scale className="w-3.5 h-3.5 text-accent" /> Riferimenti normativi
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5">
            È legale far pranzare i colleghi in ufficio?
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Domanda secca, risposta onesta. <strong className="text-foreground">Schiscetta non è un servizio di ristorazione</strong>:
            è uno <em>strumento di organizzazione</em> che aiuta i colleghi a coordinarsi.
            Capire come stare in regola però è importante — qui trovi i riferimenti normativi italiani,
            scritti in modo comprensibile, con i casi pratici più frequenti.
          </p>
        </section>

        {/* TL;DR */}
        <section className="bg-primary/5 border border-primary/20 rounded-lg p-6 sm:p-7 mb-12" data-testid="legal-tldr">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-1" />
            <div>
              <h2 className="font-display text-xl font-medium mb-2">In sintesi</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                Il pranzo <strong>occasionale, privato, tra colleghi che si conoscono</strong> non è
                attività di somministrazione al pubblico e non richiede autorizzazioni commerciali.
                Quando però la cosa diventa <strong>regolare e organizzata</strong> con un giro continuativo di
                porzioni e denaro, scattano le regole sull'igiene alimentare (HACCP) e potenzialmente
                la disciplina delle imprese alimentari.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Schiscetta serve a <strong>tenere traccia</strong> di chi prepara cosa e di chi prenota.
                Le responsabilità sulla preparazione, igiene e fiscalità del cibo restano in capo a chi lo prepara.
              </p>
            </div>
          </div>
        </section>

        {/* I 3 scenari */}
        <section className="mb-12">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
            I tre scenari più comuni
          </h2>
          <div className="space-y-4">
            <ScenarioCard
              level="ok"
              title="A. Pranzo occasionale (1 volta ogni tanto)"
              description="Un collega porta una teglia di parmigiana, condivide con chi vuole, eventualmente raccoglie qualche euro. Nessuna abitudine, nessuna organizzazione stabile."
              regulation="Iniziativa privata, estemporanea, occasionale. Esclusa dal Reg. CE 852/2004."
              actions="Nessun obbligo specifico. Buonsenso igienico standard."
            />
            <ScenarioCard
              level="warn"
              title="B. Servizio regolare tra colleghi conosciuti (lo scenario tipico di Schiscetta)"
              description="Una o più persone preparano pasti più volte alla settimana, raccolgono prenotazioni e quote stabili tra colleghi della stessa sede."
              regulation="Attività organizzata: rientra nella nozione di 'impresa alimentare' UE (Reg. 178/2002, art. 3). Anche senza fini di lucro, scatta l'autocontrollo HACCP (Reg. 852/2004)."
              actions="• Notifica all'ASL (SCIA semplificata o registrazione operatore). • Manuale di autocontrollo HACCP. • Formazione di chi prepara (corso di base). • Etichettatura allergeni (D.lgs. 231/2017)."
            />
            <ScenarioCard
              level="strict"
              title="C. Apertura ai non colleghi / fini di lucro"
              description="I pasti vengono offerti anche all'esterno, oppure il giro economico è rilevante e continuativo."
              regulation="Vera e propria somministrazione/vendita. Si applicano L. 287/1991, regolamenti regionali e disciplina fiscale dell'attività."
              actions="Apertura partita IVA, SCIA commerciale, conformità locali, contratti di lavoro se ci sono dipendenti."
            />
          </div>
        </section>

        {/* Riferimenti */}
        <section className="mb-12">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
            Le norme citate, per chi vuole approfondire
          </h2>
          <ul className="space-y-3 text-sm">
            <Ref
              title="Regolamento (CE) n. 178/2002"
              subtitle="Definisce 'impresa alimentare' come qualsiasi attività, pubblica o privata, con o senza fini di lucro, connessa a produzione, trasformazione o distribuzione di alimenti (art. 3)."
            />
            <Ref
              title="Regolamento (CE) n. 852/2004"
              subtitle="Obbligo di autocontrollo HACCP e registrazione dell'attività presso l'autorità competente. Esenzione per iniziative estemporanee, occasionali e private."
            />
            <Ref
              title="D.lgs. 6 novembre 2007, n. 193"
              subtitle="Attuazione italiana del pacchetto igiene. Definisce sanzioni e competenze ASL."
            />
            <Ref
              title="D.lgs. 15 dicembre 2017, n. 231"
              subtitle="Etichettatura e informazione sui consumatori: obbligo di indicare allergeni anche per pasti non confezionati."
            />
            <Ref
              title="Legge 25 agosto 1991, n. 287"
              subtitle="Disciplina della somministrazione al pubblico di alimenti e bevande. Si applica quando l'attività è rivolta a una platea aperta, non a colleghi privati."
            />
            <Ref
              title="Reg. (UE) n. 2017/625"
              subtitle="Controlli ufficiali sulla filiera alimentare. Le ASL operano in base a questo regolamento."
            />
          </ul>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            I testi delle norme sono consultabili gratuitamente su{" "}
            <a href="https://www.normattiva.it" target="_blank" rel="noreferrer" className="text-primary hover:underline">
              normattiva.it
            </a>{" "}
            (Italia) e{" "}
            <a href="https://eur-lex.europa.eu" target="_blank" rel="noreferrer" className="text-primary hover:underline">
              eur-lex.europa.eu
            </a>{" "}
            (UE).
          </p>
        </section>

        {/* Cosa fare in pratica */}
        <section className="mb-12 bg-card border border-border rounded-lg p-6 sm:p-8" data-testid="legal-checklist">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            <BookOpen className="w-4 h-4" /> Checklist pratica
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-5">
            Se siete nello scenario B (caso tipico)
          </h2>
          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            Vi consigliamo questo percorso minimo (variabile in base alla Regione e all'ASL territoriale):
          </p>
          <ol className="space-y-3 text-sm text-foreground/80 leading-relaxed">
            <Step n="1">
              <strong>Confronto preventivo con la vostra ASL.</strong> Spiegate il caso (es. "uno-due colleghi
              preparano pasti due-tre volte a settimana per circa 10 persone della stessa sede"). L'ASL vi dirà
              esattamente cosa richiede per la vostra Regione. È gratuito e vi mette in chiaro con tutti.
            </Step>
            <Step n="2">
              <strong>Notifica/registrazione dell'attività</strong> presso il SUAP (Sportello Unico Attività
              Produttive) del Comune. Solitamente bastano una SCIA semplificata e l'inquadramento come "attività
              alimentare a basso rischio".
            </Step>
            <Step n="3">
              <strong>Manuale HACCP semplificato</strong>: descrive ingredienti, procedure di preparazione,
              conservazione e trasporto, pulizia degli ambienti. Esistono modelli standard che si adattano a
              cucine domestiche di piccola scala.
            </Step>
            <Step n="4">
              <strong>Formazione base alimentaristi</strong> per chi cucina. Il corso dura poche ore, costa
              pochi euro, e copre rischi microbiologici, allergeni, conservazione.
            </Step>
            <Step n="5">
              <strong>Allergeni e ingredienti</strong> dichiarati ad ogni pasto. Schiscetta lo fa automaticamente:
              ogni piatto pubblicato richiede di compilare il campo "Ingredienti" e "Allergeni".
            </Step>
            <Step n="6">
              <strong>Aspetto fiscale</strong>: per giri economici molto piccoli (poche centinaia di euro/mese
              tra colleghi) il consulente del lavoro o un commercialista vi può inquadrare come prestazione
              occasionale o ricavo da hobbistica. Per giri più sostanziosi, valutare partita IVA o cooperativa.
            </Step>
          </ol>
        </section>

        {/* Cosa fa Schiscetta */}
        <section className="mb-12">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-5">
            Cosa fa Schiscetta per aiutarvi a stare in regola
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Feature title="Tracciabilità completa">
              Ogni piatto ha autore, data, ingredienti, allergeni, prezzo e numero di porzioni. In caso di
              ispezione, c'è documentazione storica esportabile in CSV.
            </Feature>
            <Feature title="Allergeni sempre dichiarati">
              Il preparatore deve compilare il campo allergeni per pubblicare. Conforme a D.lgs. 231/2017
              sull'informazione al consumatore.
            </Feature>
            <Feature title="Dati isolati per sede">
              Multi-tenancy strict: i dati di un ufficio non si mescolano con quelli di altre sedi. GDPR-friendly.
            </Feature>
            <Feature title="Niente flussi di pagamento">
              Schiscetta non gestisce il denaro tra preparatore e ordinante. Restate liberi di organizzare i
              pagamenti come fate sempre (contanti, IOU di gruppo).
            </Feature>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-12 flex items-start gap-3" data-testid="legal-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Questa pagina ha scopo informativo</strong> e riassume i principali riferimenti normativi
            italiani ed europei al momento della pubblicazione. Non sostituisce una consulenza legale o fiscale
            personalizzata. Per casi borderline, contattate l'ASL competente per il territorio e/o un
            professionista qualificato.
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8 border-t border-border">
          <p className="text-muted-foreground mb-5">
            Volete supporto per inquadrare la vostra sede e configurare Schiscetta correttamente?
          </p>
          <Link
            to="/contatti"
            data-testid="legal-cta-contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-md hover:bg-[#436343]"
          >
            Parla con noi <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center text-xs text-muted-foreground">
          Schiscetta · 2026 · Informativa giuridica · <Link to="/info" className="hover:underline">Manifesto</Link>
        </div>
      </footer>
    </div>
  );
}

function ScenarioCard({ level, title, description, regulation, actions }) {
  const palette = {
    ok: { border: "border-primary/30", chip: "bg-primary/10 text-primary", chipLabel: "Verde" },
    warn: { border: "border-amber-300", chip: "bg-amber-100 text-amber-800", chipLabel: "Giallo" },
    strict: { border: "border-accent/30", chip: "bg-accent/10 text-accent", chipLabel: "Rosso" },
  }[level];
  return (
    <article className={`bg-card border ${palette.border} rounded-lg p-5 sm:p-6`} data-testid={`scenario-${level}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-display text-lg font-medium">{title}</h3>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded shrink-0 ${palette.chip}`}>
          {palette.chipLabel}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{description}</p>
      <div className="text-xs space-y-1.5">
        <div>
          <span className="font-medium text-foreground/70">Inquadramento normativo:</span>{" "}
          <span className="text-muted-foreground">{regulation}</span>
        </div>
        <div>
          <span className="font-medium text-foreground/70">Cosa fare:</span>{" "}
          <span className="text-muted-foreground">{actions}</span>
        </div>
      </div>
    </article>
  );
}

function Ref({ title, subtitle }) {
  return (
    <li className="bg-card border border-border rounded-lg p-3.5 flex items-start gap-3">
      <ExternalLink className="w-4 h-4 text-primary mt-1 shrink-0" />
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{subtitle}</div>
      </div>
    </li>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center mt-0.5">{n}</span>
      <span>{children}</span>
    </li>
  );
}

function Feature({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="font-medium text-sm mb-1.5">{title}</div>
      <p className="text-xs text-muted-foreground leading-relaxed">{children}</p>
    </div>
  );
}
